import { z } from 'zod';
/*
  KIR-5191 — agent auto-update to latest (Phase 1 of KIR-2927).

  The agent asks `GET /api/agent/update-target` on start and every 4 hours
  (±20%). When the org switch is On, the key is valid and the org is live, the
  backend answers `update` with Knostic's latest production version, provided
  an installer exists for the machine's OS, CPU and package type. The agent
  asks the privileged system helper (`kirin helper`) to apply that version;
  the helper installs only the version the release manifest on the download
  CDN lists as latest, after checking its sha256 and the installer's OS code
  signature. The agent reports the outcome with `POST /api/agent/update-report`.
*/
/** How long a new version has to reconnect before the row reads "Needs attention". */
export const AGENT_UPDATE_HEALTH_WINDOW_MINUTES = 10;
/**
 * How long a machine may wait for the restart that finishes a Windows install
 * (`pending_reboot`) before the row reads "Needs attention". Long enough for a
 * normal reboot cycle; a machine that rebooted and still runs the old version
 * must not show "Finishes after restart" forever.
 */
export const AGENT_UPDATE_PENDING_REBOOT_HOURS = 72;
// ---------------------------------------------------------------------------
// Org setting (`settings.setting_type = 'agent-auto-update'`)
// ---------------------------------------------------------------------------
export const agentAutoUpdateConfigSchema = z.object({
    enabled: z.boolean(),
});
/** No settings row means On. */
export const AGENT_AUTO_UPDATE_DEFAULT = {
    enabled: true,
};
/** The org setting as the dashboard and the Management API read it. */
export const agentAutoUpdateSettingSchema = z.object({
    enabled: z.boolean(),
    /**
     * Knostic's kill switch (`AGENT_AUTO_UPDATE_DISABLED`) is on: no agent is
     * offered an update whatever `enabled` says. Optional so a newer dashboard
     * still parses an older backend's answer; absent reads as not paused.
     */
    pausedByKnostic: z.boolean().optional(),
    /** When the setting was last changed; null while the default applies. */
    updatedAt: z.string().datetime().nullable(),
});
export const updateAgentAutoUpdateSettingBodySchema = z.object({
    enabled: z.boolean(),
});
// ---------------------------------------------------------------------------
// Update state of one endpoint
// ---------------------------------------------------------------------------
/**
 * `off` is never stored: it is read from the org setting, so it does not lag
 * the switch. `needs_attention` is stored when the agent reports an install it
 * cannot update, and is also derived at read time from an `updating` row older
 * than {@link AGENT_UPDATE_HEALTH_WINDOW_MINUTES}. `pending_reboot` means the
 * installer finished but Windows completes it at the next restart (NSIS exit
 * code 3010); it turns `up_to_date` once the machine reports the new
 * version, and reads as `needs_attention` after
 * {@link AGENT_UPDATE_PENDING_REBOOT_HOURS}. `not_available`: the machine was
 * installed without the Kirin helper (the one-line install script, an older
 * per-user Windows copy), or its helper cannot verify installers (an unsigned
 * Linux package), so it cannot update by itself.
 */
export const agentUpdateStateSchema = z.enum([
    'up_to_date',
    'updating',
    'pending_reboot',
    'needs_attention',
    'update_failed',
    'pinned_by_mdm',
    'not_available',
    'off',
]);
/** States the backend writes to `endpoints.update_state`. */
export const storedAgentUpdateStateSchema = agentUpdateStateSchema.exclude([
    'off',
]);
/**
 * Worst first. A user with several machines shows the worst state among them.
 */
export const AGENT_UPDATE_STATE_SEVERITY = [
    'update_failed',
    'needs_attention',
    'updating',
    'pending_reboot',
    'pinned_by_mdm',
    'not_available',
    'up_to_date',
    'off',
];
// ---------------------------------------------------------------------------
// Agent ↔ backend
// ---------------------------------------------------------------------------
export const agentUpdateOsSchema = z.enum(['darwin', 'windows', 'linux']);
export const agentUpdateArchSchema = z.enum(['amd64', 'arm64']);
/** The native installer type the machine was installed with. */
export const agentUpdatePackageSchema = z.enum(['pkg', 'nsis', 'deb', 'rpm']);
const machineIdSchema = z.string().min(1).max(64);
const versionSchema = z.string().min(1).max(64);
/** Query string of `GET /api/agent/update-target`. */
export const updateTargetQuerySchema = z.object({
    machineId: machineIdSchema,
    /** The running agent's version. */
    version: versionSchema,
    os: agentUpdateOsSchema,
    arch: agentUpdateArchSchema,
    package: agentUpdatePackageSchema,
    /** The `--version` an MDM run pinned, read from `.mdm-install-state.json`. */
    mdmPinnedVersion: versionSchema.optional(),
});
/**
 * Why there is nothing to install. `unavailable` covers the release gate
 * flag, the kill switch, a missing installer for the platform and a
 * release-source outage; the agent treats them alike. `pending_reboot`: this
 * latest is already installed and finishes at the next restart, so offering
 * it again would only rerun the installer.
 */
export const updateTargetNoneReasonSchema = z.enum([
    'off',
    'up_to_date',
    'pinned_by_mdm',
    'held_after_failure',
    'pending_reboot',
    'unavailable',
]);
export const updateTargetResponseSchema = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('none'),
        reason: updateTargetNoneReasonSchema,
        latestVersion: z.string().nullable(),
    }),
    z.object({
        action: z.literal('update'),
        /** The version to apply; `kirin helper` installs nothing else. */
        latestVersion: z.string(),
    }),
]);
/** States the agent may report. */
export const updateReportStateSchema = z.enum([
    'updating',
    'pending_reboot',
    'update_failed',
    'needs_attention',
    'up_to_date',
    'not_available',
]);
/**
 * Body of `POST /api/agent/update-report`. `updating` must name its target:
 * the stored target drives the health window, the hold and completion, so a
 * new attempt without one would be judged against the previous attempt's.
 */
export const updateReportBodySchema = z
    .object({
    machineId: machineIdSchema,
    state: updateReportStateSchema,
    targetVersion: versionSchema.optional(),
    /** Short machine-readable code plus detail, e.g. `signature_invalid: …`. */
    error: z.string().max(500).optional(),
})
    .refine(body => body.state !== 'updating' || body.targetVersion, {
    message: '`targetVersion` is required when `state` is `updating`',
    path: ['targetVersion'],
});
export const updateReportResponseSchema = z.object({
    accepted: z.boolean(),
});
// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
/**
 * "Try update now" for one user's agents, or only the agent on `machineId`
 * when it is given.
 */
export const requestAgentUpdateCheckBodySchema = z.object({
    userId: z.string().uuid(),
    machineId: machineIdSchema.optional(),
});
/**
 * Why "Try update now" sent nothing: `off` — the org's switch is Off;
 * `paused` — Knostic's kill switch (`AGENT_AUTO_UPDATE_DISABLED`) is on.
 */
export const agentUpdateCheckSkipReasonSchema = z.enum(['off', 'paused']);
export const requestAgentUpdateCheckResponseSchema = z.object({
    /**
     * Whether the nudge was sent to the user's connected agents. False while
     * the org's switch is Off or Knostic's kill switch is on; `reason` says
     * which. Best effort: other server replicas relay it to their agents only
     * while Redis is reachable, and how many agents it reached is not known
     * when the request returns.
     */
    requested: z.boolean(),
    /** Set only when `requested` is false. */
    reason: agentUpdateCheckSkipReasonSchema.optional(),
});
//# sourceMappingURL=agent-auto-update.js.map